import { NodeMaterialDto } from "@api/apiClient";
import { readMultipleModelData } from "@/types/storage/modelDataStorage";
import { useEffect, useState } from "react";

export interface CachedAssignedCo2MaterialTypeIdsResult {
  ids: string[];
  /** True until the IndexedDB read for cached node materials completes */
  isLoading: boolean;
}

/**
 * Reads co2MaterialTypeIds from IndexedDB-cached node materials so the by-ids
 * emission-factor fetch can start before the server round-trip completes.
 */
export function useCachedAssignedCo2MaterialTypeIds(
  modelIds: string[],
  scenarioId: string | undefined
): CachedAssignedCo2MaterialTypeIdsResult {
  const modelIdsKey = modelIds.join(",");
  const [cachedIds, setCachedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(() => Boolean(scenarioId && modelIds.length > 0));

  useEffect(() => {
    if (!scenarioId || modelIds.length === 0) {
      setCachedIds([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      const ids = new Set<string>();
      try {
        const cachedByModel = await readMultipleModelData<[number, NodeMaterialDto][] | NodeMaterialDto[]>(
          "scenarioNodeMaterials",
          modelIds,
          scenarioId
        );

        cachedByModel.forEach((entries) => {
          if (!entries || entries.length === 0) return;

          const materials: NodeMaterialDto[] = Array.isArray(entries[0])
            ? (entries as [number, NodeMaterialDto][]).map(([, dto]) => dto)
            : (entries as NodeMaterialDto[]);

          for (const material of materials) {
            if (material.co2MaterialTypeId) {
              ids.add(material.co2MaterialTypeId);
            }
          }
        });
      } catch (err) {
        console.warn("[useCachedAssignedCo2MaterialTypeIds] Failed to read IDB cache:", err);
      }

      if (!cancelled) {
        setCachedIds(Array.from(ids));
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [modelIdsKey, scenarioId]);

  return { ids: cachedIds, isLoading };
}
